// geschaeftsjahr.ts — Lesen und Schreiben des Geschäftsjahr-Stichtags
// (`Contact.financialYearEnd`). DOM-frei, damit die Formate in node prüfbar sind.
//
// Der Stichtag ist jahresunabhängig und wird als "TT.MM" gespeichert. Die
// Bestandsdaten halten sich nicht durchgängig daran: gewachsen sind unter
// anderem "01.01." (mit abschließendem Punkt), und über Import- und
// Umwandlungswege können ein volles Datum ("31.12.2024"), ein ISO-Datum
// ("2024-12-31") oder einstellige Angaben ("1.1") entstehen.
//
// Warum das zählt: die beiden Auswahlfelder im Formular zeigen einen Wert nur,
// wenn er BUCHSTÄBLICH zu einer ihrer Optionen passt ("01".."31" bzw.
// "01".."12"). Ein nicht erkanntes Format stellt sich deshalb als LEERES Feld
// dar — der Wert steht in der Datenbank, ist aber unsichtbar. Wer das Formular
// danach speichert, überschreibt ihn stillschweigend mit „nichts". Diese
// Normalisierung schließt genau diese Lücke.

/** Zerlegter Stichtag; leere Strings heißen „nicht gesetzt". */
export type Geschaeftsjahr = { tag: string; monat: string };

export const GJ_MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/**
 * Tage des Monats — Februar mit 29, weil der Stichtag jahresunabhängig ist und
 * ein Schaltjahr sonst nicht wählbar wäre.
 */
export const gjDaysInMonth = (monat: string): number =>
  [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][(parseInt(monat, 10) || 1) - 1] ?? 31;

const pad = (n: number): string => String(n).padStart(2, "0");

/** Zahl im gültigen Bereich → zweistellig; sonst "". */
const inRange = (raw: string, min: number, max: number): string => {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= min && n <= max ? pad(n) : "";
};

/**
 * Liest einen gespeicherten Stichtag in Tag + Monat.
 *
 * Erkannt werden "TT.MM", "TT.MM.", "TT.MM.JJJJ", einstellige Varianten wie
 * "1.1" sowie ISO-Datumsangaben ("2024-12-31", auch mit Zeitanteil). Alles
 * andere liefert einen leeren Stichtag — besser ein sichtbar leeres Feld als
 * eine falsche Anzeige.
 */
export function parseGeschaeftsjahr(value: unknown): Geschaeftsjahr {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return { tag: "", monat: "" };

  // ISO zuerst: dort steht der Monat VOR dem Tag, eine Verwechslung wäre still
  // und würde aus dem 31.12. den 12.31. machen.
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const monat = inRange(iso[2], 1, 12);
    const tag = inRange(iso[3], 1, 31);
    return monat && tag ? { tag, monat } : { tag: "", monat: "" };
  }

  const dot = raw.match(/^(\d{1,2})\.(\d{1,2})\.?/);
  if (dot) {
    const tag = inRange(dot[1], 1, 31);
    const monat = inRange(dot[2], 1, 12);
    return tag && monat ? { tag, monat } : { tag: "", monat: "" };
  }

  return { tag: "", monat: "" };
}

/**
 * Tag + Monat → Speicherform "TT.MM". Ein halber Stichtag (nur Tag oder nur
 * Monat) ergibt bewusst "" — ein Bilanzstichtag ohne Monat ist keine Angabe,
 * und ein Teilwert in der Datenbank wäre beim nächsten Lesen wieder unlesbar.
 */
export function formatGeschaeftsjahr(tag: string, monat: string): string {
  const t = inRange(tag, 1, 31);
  const m = inRange(monat, 1, 12);
  return t && m ? `${t}.${m}` : "";
}

/**
 * Die wählbaren Tage für einen Monat — einschließlich des bereits
 * gespeicherten Tages, auch wenn er über dem Monatsmaximum liegt.
 *
 * Ohne diesen Zusatz verschwände ein Bestandswert wie „31.09." aus der
 * Anzeige (September hat 30 Optionen), und das Feld sähe leer aus, obwohl ein
 * Wert da ist. Sichtbar bleiben heißt hier: korrigierbar bleiben.
 */
export function gjTagOptionen(monat: string, aktuellerTag?: string): string[] {
  const max = gjDaysInMonth(monat || "12");
  const tage = Array.from({ length: max }, (_, i) => pad(i + 1));
  const t = inRange(aktuellerTag ?? "", 1, 31);
  if (t && !tage.includes(t)) tage.push(t);
  return tage;
}
